import { ChatMessages } from "@storytell/cp-client";
import { getBlockContent, type StreamingMessageId } from "@storytell/streaming";
import type { ChatRuntime } from "../runtime/ChatRuntime";
import { parseAskUserData } from "./askUserSchema";

/**
 * Whether a message is parked on a question the user hasn't answered yet.
 *
 * The workflow emits its conversation-complete operation when the agent stops,
 * and asking the user is one of the ways it stops — so a turn waiting on an
 * answer looks finished to anything reading that operation alone. It isn't:
 * nothing happens until the user replies. Callers use this to avoid telling the
 * user a turn they are being asked to unblock is already done.
 */
export function messageAwaitsUserInput(rt: ChatRuntime, messageId: StreamingMessageId): boolean {
  const message = rt.store.message(messageId);
  if (!message) return false;

  const asked = (message.operations ?? []).some((operationId) =>
    (rt.store.operation(operationId)?.blocks ?? []).some((blockId) => {
      const block = rt.store.block(blockId);
      if (!block) return false;
      const raw = block.finalContent?.type === "text" ? block.finalContent.text : getBlockContent(blockId);
      return Boolean(raw && parseAskUserData(raw)?.awaitingUserInput);
    })
  );
  if (!asked) return false;

  // The answer arrives as the next user message on the chat, which is also how
  // the question card itself decides it has been answered.
  const chat = rt.store.chat(message.chatId);
  const at = chat?.messages?.indexOf(message.id) ?? -1;
  if (at === -1) return true;
  return !chat?.messages?.slice(at + 1).some((id) => rt.store.message(id)?.role === ChatMessages.Role.USER);
}
